import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { CheckCircle2, Flag, X } from 'lucide-react';
import { DEFAULT_FLAG_COLOR, FLAG_COLOR_OPTIONS } from './businessCardFlagColors';

function BusinessFlagColorModal({ business, selectedColor, isOpen, error, onClose, onSelectColor }) {
  useEffect(() => {
    if (!isOpen) {
      return undefined;
    }

    const { body } = document;
    const previousOverflow = body.style.overflow;

    body.style.overflow = 'hidden';

    return () => {
      body.style.overflow = previousOverflow;
    };
  }, [isOpen]);

  if (!isOpen || !business) {
    return null;
  }

  const activeColor = selectedColor === 'dark-silver'
    ? 'forest-green'
    : selectedColor || DEFAULT_FLAG_COLOR;

  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-[rgba(17,17,17,0.26)] px-4 py-6 backdrop-blur-[4px]">
      <div className="max-h-[calc(100dvh-3rem)] w-full max-w-[560px] overflow-y-auto rounded-[28px] border border-[#e9e0d8] bg-[linear-gradient(180deg,#fdfcfb_0%,#faf6f1_100%)] shadow-[0_24px_60px_rgba(17,17,17,0.14)]">
        <div className="border-b border-[#ebe3db] px-5 py-5 sm:px-6">
          <div className="flex items-start justify-between gap-4">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-4">
                <span className="flex h-[52px] w-[52px] shrink-0 items-center justify-center rounded-[18px] border border-[#e7ddd3] bg-[#f7f4ef] text-[#6f5644]">
                  <Flag className="h-[22px] w-[22px]" />
                </span>
                <div className="min-w-0">
                  <h3 className="m-0 text-[1.35rem] font-semibold tracking-[-0.03em] text-[#111111]">
                    Select Portal Theme
                  </h3>
                  <p className="mt-1 text-sm leading-5 text-[#7f736b]">
                    Choose the portal theme for <span className="font-semibold text-[#111111]">{business.name}</span>.
                  </p>
                </div>
              </div>
            </div>
            <button
              aria-label="Close portal theme modal"
              className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-[#e5dfd8] bg-white text-[#7f736b] shadow-[0_10px_24px_rgba(17,17,17,0.05)] transition hover:bg-[#faf7f3]"
              type="button"
              onClick={onClose}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {error ? (
          <div className="mx-5 mt-5 rounded-[18px] border border-[#f0d4cb] bg-[#fff8f6] px-4 py-3 text-sm text-[#b4533f] sm:mx-6">
            {error}
          </div>
        ) : null}

        <div className="grid gap-3 px-5 py-5 sm:grid-cols-2 sm:px-6">
          {FLAG_COLOR_OPTIONS.map((option) => {
            const isSelected = option.value === activeColor;

            return (
              <button
                key={option.value}
                className={`flex items-center gap-3 rounded-[18px] border bg-white px-4 py-3 text-left transition hover:border-[#cfc2b6] hover:bg-[#faf7f3] ${
                  isSelected
                    ? 'border-[#6f5644] bg-[#fbf8f4] shadow-[0_10px_22px_rgba(111,86,68,0.12)]'
                    : 'border-[#e6ddd4]'
                }`}
                type="button"
                onClick={() => onSelectColor(option.value)}
              >
                <span
                  className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full border shadow-[0_8px_16px_rgba(17,17,17,0.10)] ${
                    isSelected ? 'border-[#6f5644] ring-2 ring-[#6f5644]/25' : 'border-white'
                  }`}
                  style={{ backgroundColor: option.swatch }}
                >
                  {isSelected ? <CheckCircle2 className="h-4.5 w-4.5 text-white drop-shadow" /> : null}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold text-[#111111]">{option.label}</span>
                  <span className="mt-0.5 block text-xs text-[#8b7a6d]">
                    {isSelected
                      ? 'Current selected theme'
                      : option.value === 'default'
                        ? 'Use the current portal colors'
                        : 'Apply to this business portal'}
                  </span>
                </span>
                {isSelected ? (
                  <span className="inline-flex shrink-0 items-center gap-1.5 rounded-full border border-[#dbeadf] bg-[#edf7ef] px-2 py-1 text-[11px] font-semibold uppercase tracking-[0.06em] text-[#4e7b56]">
                    <CheckCircle2 className="h-3.5 w-3.5" />
                    Selected
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </div>
    </div>,
    document.body
  );
}

export default BusinessFlagColorModal;
